import React, { ChangeEvent, FC, useCallback, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Spacer } from 'components/Utility'
import { Description, MainHeadline } from 'components/Typography'
import { FlexRow } from 'components/Flex'
import { TextInput } from 'components/Form'
import { DeleteIconButton, NavButton, OrangeButton, TextButton } from 'components/Button'
import { FormLabel } from 'components/Label'
import { useDispatch, useSelector } from 'react-redux'
import { inviteOwners } from 'reducks/staff/operations'
import { State } from 'index'

const InviteOwnerTemplate: FC = () => {
  const dispatch = useDispatch()

  const path = useSelector((state: State) => state.router.location.pathname)
  const isInvitationPage = /\/admin\/shops\/.+\/invite_owner/.test(path)
  const storeId = isInvitationPage ? path.split('/admin/shops/')[1].split('/invite_owner')[0] : ''

  const [emails, setEmails] = useState<string[]>([''])
  const [errors, setErrors] = useState<string[]>([''])

  const addEmailRow = useCallback(() => {
    setEmails((prevState) => [...prevState, ''])
    setErrors((prevState) => [...prevState, ''])
  }, [setEmails, setErrors])

  const deleteEmailRow = useCallback(
    (index: number) => {
      setEmails((prevState) => [...prevState.filter((_, i) => i !== index)])
      setErrors((prevState) => [...prevState.filter((_, i) => i !== index)])
    },
    [setEmails, setErrors]
  )

  const handleEmails = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number) => {
      setEmails((prevState) => {
        const nextState = prevState
        nextState[index] = event.target.value
        return [...nextState]
      })
    },
    [setEmails]
  )

  const isValidEmail = useCallback(
    (email: string, index: number) => {
      const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
      const ret = regex.test(email)
      setErrors((prevState) => {
        const nextState = prevState
        nextState[index] = ret ? '' : 'メールアドレスの形式が正しくありません'
        return [...nextState]
      })
    },
    [setErrors]
  )

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'ログインアカウントを作成'} />
      <Spacer size={28} />
      <Description
        description={
          'ログインを許可するメールアドレスを入力してください。アカウント登録のためのメールが先方に送信されます。'
        }
      />
      <Spacer size={48} />
      {emails.map((email, index) => {
        return (
          <React.Fragment key={index.toString()}>
            <FormLabel label={'メールアドレス'} required={true} />
            <Spacer size={8} />
            <FlexRow justifyContent={'start'}>
              <TextInput
                disabled={false}
                error={errors[index]}
                onBlur={() => isValidEmail(email, index)}
                onChange={(e) => handleEmails(e, index)}
                required={true}
                type={email}
                value={email}
              />
              <Spacer size={8} axis={'horizontal'} />
              <DeleteIconButton disabled={index === 0} onClick={() => deleteEmailRow(index)} />
            </FlexRow>
            <Spacer size={24} />
          </React.Fragment>
        )
      })}
      <FlexRow justifyContent={'center'}>
        <TextButton
          disabled={emails.some((email) => email === '')}
          iconPath={emails.some((email) => email === '') ? '/icon/grey/plus.svg' : '/icon/blue/plus.svg'}
          label={'メールアドレスを追加する'}
          onClick={addEmailRow}
        />
      </FlexRow>
      <Spacer size={48} />
      <OrangeButton
        disabled={emails.some((email) => email === '') || errors.some((error) => error !== '')}
        label={'送信する'}
        onClick={() => dispatch(inviteOwners(emails, storeId, setErrors))}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/admin/shops'} />
      <Spacer size={72} />
    </div>
  )
}

export default InviteOwnerTemplate
